import { AxiosPromise } from 'axios';
import { AnyAction, Dispatch } from 'redux';

import { setup } from '../../../config/server/server.config';
import {
  CompanyI,
  CompanyResp,
  CompanyRespWithSettingAttributes,
  CompanySettingsAttrsI,
} from '../../interfaces/company.interface';
import { ContentLocalesI, SessionUserI, TimezonesI, UserPermissions } from '../../interfaces/session.types';
import { UserCorePatch, UserSettingsAttrs } from '../../interfaces/user.interface';
import { fetchAttributesList, resetAttributes } from '../../modules/attributes/actions/attributes.actions';
import { resetScales } from '../../modules/scales/actions/scaleActions';
import { resetSchedules } from '../../modules/surveys/actions/schedulesActions';
import { resetSurveys } from '../../modules/surveys/actions/surveyActions';
import { resetTopics } from '../../modules/topics/actions/topicActions';
import { resetUsers } from '../../modules/users/actions/user.actions';
import { ChangePasswordValues } from '../../services/api/base';
import { baseApi, defaultBaseApi } from '../../services/ApiController';
import { AsyncAction } from '../../services/apiMiddleware';
import logger from '../../services/logger/LlsLogger';
import { getBrowserPreferredSystemLocale, getBrowserTimezone } from '../../services/session';
import { resetCycles } from '../../shared/sidebar/actions/sidebarActions';
import { SHOW_NOTIFICATION } from '../app/actionTypes';
import { resetMatrix } from '~src/store/heatmap/actions';

import * as types from './actionTypes';
import { LoginUserCredentials, RedeemTokenRespI, SignupFormValuesI } from './interfaces';

export const setSessionUser = (user: SessionUserI | null): AnyAction => ({
  type: types.SET_SESSION_USER,
  user,
});

export const setUserPermissions = (permissions: UserPermissions[]): AnyAction => ({
  type: types.SET_USER_PERMISSIONS,
  permissions,
});

export const patchSessionUser = (userId: string, values: UserCorePatch): AsyncAction => ({
  types: types.PatchSessionUser,
  callAPI: () => baseApi.patch(`/users/${userId}`, values),
});

export const updateSessionUserAttrs = (userId: string, values: UserSettingsAttrs): AsyncAction => ({
  types: types.UpdateSessionUserAttrs,
  callAPI: () => baseApi.post(`/users/${userId}/attributes`, values),
});

export const updateSessionUserImage = (imageUrl: string) => (dispatch: Dispatch) => {
  dispatch({
    type: SHOW_NOTIFICATION,
    payload: { txtKey: 'x.profile_settings.image_updated_message' },
  });
  return dispatch({
    type: types.UPDATE_SESSION_USER_IMAGE,
    imageUrl,
  });
};

export const setCurrentCompany = (company: CompanyI | null): AnyAction => ({
  type: types.SET_CURRENT_COMPANY,
  company,
});

export const updateCompanyImage = (imageUrl: string) => (dispatch: Dispatch) => {
  dispatch({
    type: SHOW_NOTIFICATION,
    payload: { txtKey: 'x.company_settings.image_updated_message' },
  });
  return dispatch({
    type: types.UPDATE_COMPANY_IMAGE,
    imageUrl,
  });
};

export const updateCompany = (company: { name?: string }): AsyncAction<CompanyResp> => ({
  types: types.UpdateCompany,
  callAPI: () => baseApi.patch('/company', company),
});

export const updateCompanyAttrs = (
  attributes: CompanySettingsAttrsI,
): AsyncAction<CompanyRespWithSettingAttributes> => ({
  types: types.UpdateCompanyAttrs,
  callAPI: () => baseApi.post('/company/attributes', attributes),
});

export const loggedOut: AnyAction = { type: types.LOGGED_OUT };
export const setRedirect = (redirect?: string): AnyAction => ({ type: types.REDIRECT_SET, redirect });
export const multipleAccounts = (tokens: string[]): AnyAction => ({ type: types.MULTIPLE_ACCOUNTS, tokens });
export const clearMultipleAccounts = (): AnyAction => ({ type: types.CLEAR_MULTIPLE_ACCOUNTS });
export const multiLoginLoading = (): AnyAction => ({ type: types.MULTI_LOGIN_LOADING });
export const multiLoginNotLoading = (): AnyAction => ({ type: types.MULTI_LOGIN_NOT_LOADING });
export const loginLoading: AnyAction = { type: types.LOGIN_LOADING };
export const loginNotLoading = (): AnyAction => ({ type: types.LOGIN_NOT_LOADING });
export const loginSuccess = (): AnyAction => ({ type: types.LOGIN_SUCCESS });
export const loginFailed = (): AnyAction => ({ type: types.LOGIN_FAILED });
export const accountSuspended: AnyAction = { type: types.ACCOUNT_SUSPENDED };

export const getUserDetails = () => async (dispatch: Dispatch) => {
  const companyResponse = await baseApi
    .get<CompanyI>('/company?setting_attributes=1&permissions=1')
    .then(({ data }) => {
      dispatch(setCurrentCompany(data));
      return data;
    });

  const sessionUserResp = await baseApi
    .get<SessionUserI>('/me?setting_attributes=1&permissions=1&manager_attributes=1&extended=1')
    .then(({ data }) => {
      dispatch(setSessionUser(data));
      dispatch(setUserPermissions(data.userPermissions));
      return data;
    })
    .catch(error => {
      logger.info('Error on getting user details', {
        error,
        info: {
          componentStack: 'session/actions.ts',
        },
      });
      throw error;
    });
  localStorage.setItem('companyId', companyResponse.uuid);
  if (typeof sessionUserResp === 'object') {
    localStorage.setItem('userId', sessionUserResp.uuid);
    localStorage.setItem('userEmail', sessionUserResp.email);
  }
  return { companyResponse, sessionUserResp };
};

export const loginUser = (credentials: LoginUserCredentials) => (dispatch: any) => {
  dispatch(loginLoading);
  return baseApi
    .post<string[]>('/authenticate', credentials)
    .then(response => {
      if (response.data.length > 1) {
        dispatch(multipleAccounts(response.data));
      } else {
        dispatch(initUserLogin(response.data[0]));
      }
    })
    .catch(error => {
      logger.info('Error on login user', {
        error,
        info: {
          componentStack: 'session/actions.ts',
        },
      });
      dispatch(loginNotLoading());
      dispatch(loginFailed());
      throw error;
    });
};

export const initUserLogin = (token: string) => (dispatch: any) => {
  dispatch(loginLoading);
  localStorage.setItem('jwt', token);
  createSpaceloopSsoIframe(token);

  const fetchAttributesListPromise = dispatch(fetchAttributesList());
  const getUserDetailsPromise = dispatch(getUserDetails());
  dispatch(clearMultipleAccounts());

  return Promise.all([fetchAttributesListPromise, getUserDetailsPromise])
    .then(resp => {
      dispatch(loginSuccess());
      return resp;
    })
    .finally(() => dispatch(loginNotLoading()));
};

export const logout = (dispatch: Dispatch): AnyAction => {
  localStorage.removeItem('jwt');
  localStorage.removeItem('userId');
  localStorage.removeItem('companyId');
  createSpaceloopSsoIframe('');
  dispatch(loggedOut);
  dispatch(setSessionUser(null));
  dispatch(setUserPermissions([]));
  dispatch(setCurrentCompany(null));
  dispatch(resetCycles());
  dispatch(resetSurveys());
  dispatch(resetSchedules());
  dispatch(resetTopics());
  dispatch(resetScales());
  dispatch(resetUsers());
  dispatch(resetAttributes());
  dispatch(resetMatrix());
  return loggedOut;
};

function createSpaceloopSsoIframe(token: string) {
  const i = document.createElement('iframe');
  i.height = '0';
  i.width = '0';
  i.frameBorder = '0';
  i.src = `${setup.SPACELOOP_FE}sso.html?token=${token}`;
  document.body.appendChild(i);
}

export const updateSessionUserAttr = (key: string, value: string | number | null): AnyAction => ({
  type: types.UPDATE_SESSION_USER_ATTR,
  payload: { key, value },
});

export const signupCompany = (companyData: SignupFormValuesI): AsyncAction => {
  const postData = {
    ...companyData,
    timezone: getBrowserTimezone(),
    locale: getBrowserPreferredSystemLocale(),
  };
  return {
    types: types.SignupCompany,
    callAPI: () => baseApi.post('/register', postData),
  };
};

export const fetchTimeZones = (): AsyncAction<TimezonesI> => ({
  types: types.GetTimeZones,
  callAPI: () => baseApi.get('/timezones'),
});

export const fetchLocales = (): AsyncAction<ContentLocalesI> => ({
  types: types.GetLocales,
  callAPI: () => baseApi.get('/locales'),
});

export const redeemSessionToken =
  (token?: string): (() => Promise<RedeemTokenRespI>) =>
  () =>
    baseApi
      .post(`/authenticate/${token}`)
      .then(({ data }) => data)
      .catch(error => {
        logger.info('Error on redeem token', {
          error,
          info: {
            componentStack: 'session/actions.ts',
          },
        });
      });

export const activateAccount =
  (token?: string): (() => AxiosPromise) =>
  () =>
    baseApi.post('/activate', null, { headers: { Authorization: `Bearer ${token}` } });

export const checkActivationStatus =
  (token?: string, location?: string): (() => AxiosPromise) =>
  () =>
    baseApi
      .get('/activation/status?location=' + (location || ''), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => data)
      .catch(error => {
        logger.info('Error on checking activation status', {
          error,
          info: {
            componentStack: 'session/actions.ts',
          },
        });
        return error;
      });

export const setPassword = (values: ChangePasswordValues): AsyncAction<void> => ({
  types: types.ChangePassword,
  callAPI: () => defaultBaseApi.passwordSet(values),
});

export const getNewPassword = (values: { email: string }) => defaultBaseApi.passwordResetForEmail(values);
